import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  linkedSignal,
  output,
  signal,
  Signal,
  TemplateRef,
  untracked,
  viewChild,
  WritableSignal
} from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { AbstractControl, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { DetailPopupMetadataFooterComponent, FormControlHintType, FormControlType, FormField, FormGroupComponent } from '@iot-platform/iot-platform-ui';
import { NameValidators } from '@iot-platform/iot-platform-utils';
import { BusinessProfile, FAVORITE_VIEW_COLORS, FAVORITE_VIEW_DEFAULT_COLOR, FavoriteView } from '@iot-platform/models/common';
import { TranslatePipe } from '@ngx-translate/core';
import { get, isEqual, mapValues } from 'lodash';
import { of, switchMap } from 'rxjs';
import { FavoriteViewFormControlNames } from '../../../models';
import { FavoriteViewsService } from '@iot-platform/data-access/favorite-views';
import { AuthFacade } from '@iot-platform/auth';

@Component({
  selector: 'iot-platform-ui-favorite-view-information-form',
  imports: [FormGroupComponent, TranslatePipe, ReactiveFormsModule, MatCheckboxModule, DetailPopupMetadataFooterComponent],
  templateUrl: './information-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FavoriteViewInformationFormComponent {
  private readonly favoriteViewsService: FavoriteViewsService = inject(FavoriteViewsService);
  private readonly authFacade: AuthFacade = inject(AuthFacade);
  private readonly destroyRef: DestroyRef = inject(DestroyRef);

  favoriteView = input<FavoriteView | undefined>();
  masterView = input<string>();
  createForBusinessProfile = input<boolean>(false);

  formValue = output<Partial<FavoriteView> | null>();
  formStateChanged = output<boolean>();

  form: FormGroup = new FormGroup({});
  formChanges: Signal<unknown> = toSignal(this.form.events.pipe(takeUntilDestroyed(this.destroyRef)));
  initialFormState: WritableSignal<unknown> = signal(null);
  colorOptionTpl = viewChild<TemplateRef<{ item: string }>>('colorOptionTpl');

  businessProfiles: Signal<BusinessProfile[]> = toSignal(
    toObservable(this.authFacade.canReadBusinessProfile).pipe(
      switchMap((canReadBusinessProfiles) => {
        if (canReadBusinessProfiles) {
          return this.favoriteViewsService.getBusinessProfiles();
        }
        const selectedBusinessProfile = this.authFacade.selectedBusinessProfile();
        return of(selectedBusinessProfile ? [selectedBusinessProfile] : []);
      }),
      takeUntilDestroyed(this.destroyRef)
    )
  );

  pinned: WritableSignal<boolean> = linkedSignal<FavoriteView, boolean>({
    source: this.favoriteView,
    computation: (value) => value?.pinned ?? false
  });

  shared: WritableSignal<boolean> = linkedSignal<{ favoriteView: FavoriteView | undefined; createForBusinessProfile: boolean }, boolean>({
    source: () => ({ favoriteView: this.favoriteView(), createForBusinessProfile: this.createForBusinessProfile() }),
    computation: ({ favoriteView, createForBusinessProfile }) => (favoriteView?.shared || createForBusinessProfile) ?? false
  });

  sharedDisabled = computed(() => !!this.favoriteView()?.shared || this.createForBusinessProfile());

  initFormEffect = effect(() => {
    this.favoriteView();
    untracked(() => {
      this.initForm();
      this.initFormInitialState();
      if (!this.favoriteView()?.id && this.favoriteView()?.name) {
        this.name.markAsTouched();
      }
    });
  });

  initBusinessProfileEffect = effect(() => {
    const businessProfileId = this.favoriteView()?.businessProfileId ?? this.authFacade.selectedBusinessProfile()?.id;
    const businessProfiles = this.businessProfiles();
    untracked(() => {
      this.businessProfile.patchValue(businessProfiles?.find((bp) => bp.id === businessProfileId) ?? null);
      this.name?.updateValueAndValidity({ onlySelf: true, emitEvent: true });
      this.initFormInitialState();
    });
  });

  emitFormValueEventEffect = effect(() => {
    this.formChanges();
    const formValid = this.form.valid;
    const formValueChanged = this.formValueChanged();
    this.formStateChanged.emit(formValueChanged);
    if (formValid) {
      this.formValue.emit({
        name: this.name.getRawValue(),
        color: this.color.getRawValue(),
        businessProfileId: this.businessProfile.getRawValue()?.id ?? null,
        pinned: this.pinned(),
        shared: this.shared(),
        description: this.description.getRawValue()?.trim?.() || null
      });
    } else {
      this.formValue.emit(null);
    }
  });

  fields: WritableSignal<Partial<FormField>[]> = signal([
    {
      type: FormControlType.TEXT,
      name: signal(FavoriteViewFormControlNames.NAME),
      label: signal('FAVORITE_VIEW.FORM.PLACEHOLDER_NAME'),
      minLength: signal(1),
      maxLength: signal(50),
      hint: signal({ type: signal(FormControlHintType.MAX_LENGTH) }),
      required: signal(true),
      fxFlex: signal('100%')
    },
    {
      type: FormControlType.DROP_DOWN_LIST,
      name: signal(FavoriteViewFormControlNames.BUSINESS_PROFILE),
      label: signal('FAVORITE_VIEW.FORM.BUSINESS_PROFILE'),
      required: signal(true),
      disabled: computed(() => !this.createForBusinessProfile()),
      fxFlex: signal('100%'),
      items: this.businessProfiles,
      showSpinner: computed(() => !this.businessProfiles()),
      trackBy: (item: BusinessProfile) => item.id,
      displayBy: (item: BusinessProfile) => item?.name ?? '',
      compareFn: (a: BusinessProfile, b: BusinessProfile) => a?.id === b?.id,
      selectionChange: ({ value }: { value: BusinessProfile | null }) => {
        this.businessProfile?.patchValue(value);
        this.name?.updateValueAndValidity({ onlySelf: true, emitEvent: true });
      }
    },
    {
      type: FormControlType.DROP_DOWN_LIST,
      name: signal(FavoriteViewFormControlNames.COLOR),
      label: signal('TAG_EDITOR.COLOR'),
      fxFlex: signal('100%'),
      required: signal(true),
      items: signal(FAVORITE_VIEW_COLORS),
      displayBy: (item: string) => item,
      compareFn: (a: string, b: string) => a === b,
      selectDisplayWrapperRef: this.colorOptionTpl,
      selectTriggerRef: this.colorOptionTpl
    },
    {
      type: FormControlType.TEXT_AREA,
      name: signal(FavoriteViewFormControlNames.DESCRIPTION),
      label: signal('DEVICES.INFO_FORM.DESCRIPTION'),
      required: signal(false),
      maxLength: signal(200),
      fxFlex: signal('100%'),
      hint: signal({ type: signal(FormControlHintType.MAX_LENGTH) })
    }
  ]);

  formValueChanged: Signal<boolean> = computed(() => {
    this.formChanges();
    const initialFormState = this.initialFormState();
    const currentFormState = mapValues(this.form.getRawValue(), (v) => (v === '' ? null : v));
    const pinnedChanged = this.pinned() !== (this.favoriteView()?.pinned ?? false);
    const sharedChanged = this.shared() !== (this.favoriteView()?.shared ?? false);
    return !isEqual(currentFormState, initialFormState) || pinnedChanged || sharedChanged;
  });

  get businessProfile(): AbstractControl {
    return this.form.get(FavoriteViewFormControlNames.BUSINESS_PROFILE);
  }

  get name(): AbstractControl {
    return this.form.get(FavoriteViewFormControlNames.NAME);
  }

  get color(): AbstractControl {
    return this.form.get(FavoriteViewFormControlNames.COLOR);
  }

  get description(): AbstractControl {
    return this.form.get(FavoriteViewFormControlNames.DESCRIPTION);
  }

  onBusinessProfileChange(businessProfile: BusinessProfile | null): void {
    this.businessProfile?.patchValue(businessProfile);
    this.name?.updateValueAndValidity({ onlySelf: true, emitEvent: true });
  }

  private initForm(): void {
    this.form.addControl(
      FavoriteViewFormControlNames.NAME,
      new FormControl<string>(get(this.favoriteView(), 'name'), {
        validators: [Validators.maxLength(50), Validators.required, Validators.pattern('\\S.*')],
        asyncValidators: [
          NameValidators.asyncUniqueNameValidatorDynamic(this.favoriteViewsService, this.favoriteView()?.id ? (this.favoriteView()?.name ?? '') : '', () => [
            (this.businessProfile?.getRawValue() as BusinessProfile | null)?.id ?? null,
            this.masterView() ?? '',
            this.shared()
          ])
        ]
      })
    );
    this.form.addControl(FavoriteViewFormControlNames.BUSINESS_PROFILE, new FormControl<string>(null));
    this.form.addControl(FavoriteViewFormControlNames.COLOR, new FormControl<string>(get(this.favoriteView(), 'color', FAVORITE_VIEW_DEFAULT_COLOR)));
    this.form.addControl(FavoriteViewFormControlNames.DESCRIPTION, new FormControl<string>(get(this.favoriteView(), 'description', null)));
  }

  private initFormInitialState(): void {
    this.initialFormState.set(this.form.getRawValue());
  }
}
